import React from "react";
import { Bot, Menu, Volume2, VolumeX, Sparkles, Mic, MicOff, FileText, Layers, LogOut } from "lucide-react";
import { Link } from "react-router-dom";

/**
 * Claude v2.4-inspired chat header — branded with tenant's logo + theme_color.
 *
 * Consultative Experience v1 additions:
 *   - Brief / Elaborate response-mode toggle (healthcare tenants).
 *   - Voice Chat entry point with pulsing dot when idle and live "listening" state.
 *
 * Final Polish v1 additions:
 *   - Right-cluster order: Voice → Brief → Elaborate → Speaker → Get in touch → Exit.
 *   - "Exit" CTA returns the visitor to the marketing landing page (or invokes
 *     `onExit` if supplied) without breaking the chat container.
 *
 * Pure presentational component. All backend state lives in the parent.
 */
export default function ChatHeader({
  name,
  logoUrl,
  ttsOn,
  onToggleTts,
  onToggleSidebar,
  onOpenLead,
  onExit,
  isEmbed = false,
  isInternalRudras = false,
  variant = "",
  // Consultative Experience v1
  responseMode,           // "brief" | "elaborate"
  onSetResponseMode,
  voiceEnabled = false,
  listening = false,
  onToggleVoice,
}) {
  const fullLogo = logoUrl ? `${process.env.REACT_APP_BACKEND_URL}${logoUrl}` : "";
  const headerCls = `rch-hdr${variant ? ` rch-hdr-${variant}` : ""}`;
  const subLabel = variant === "healthcare" ? "Online · Healthcare AI Sales Engineer" : "Online · AI Assistant";
  const showResponseMode = variant === "healthcare" && typeof onSetResponseMode === "function";
  return (
    <header className={headerCls} data-testid="rch-header">
      {!isEmbed && (
        <button
          className="rch-hdr-icon-btn rch-sidebar-toggle"
          onClick={onToggleSidebar}
          aria-label="Toggle sidebar"
          data-testid="rch-sidebar-toggle"
        >
          <Menu className="w-4 h-4" />
        </button>
      )}
      <div className="rch-hdr-logo">
        <div className="rch-hdr-avatar">
          {fullLogo ? <img src={fullLogo} alt="" /> : <Bot className="w-4 h-4 text-white" />}
        </div>
        <div className="min-w-0">
          <div className="rch-hdr-name" data-testid="chat-company-name">{name}</div>
          <div className="rch-hdr-sub">
            <span className="rch-online">
              <span className="rch-online-dot" /> {subLabel}
            </span>
          </div>
        </div>
      </div>
      <div className="rch-hdr-right">
        {/* 1. Voice chat — moved BEFORE Brief/Elaborate per Final Polish v1 spec */}
        {voiceEnabled && typeof onToggleVoice === "function" && (
          <button
            type="button"
            className={`rch-voice-cta ${listening ? "is-listening" : ""}`}
            onClick={onToggleVoice}
            aria-pressed={listening}
            data-testid="rch-voice-cta"
            title={listening ? "Click to stop" : "Click to speak"}
          >
            <span className="rch-voice-cta-icon">
              {listening ? <MicOff className="w-3.5 h-3.5" /> : <Mic className="w-3.5 h-3.5" />}
            </span>
            <span className="rch-voice-cta-label">
              {listening ? "Listening…" : "Voice chat"}
            </span>
            {!listening && <span className="rch-voice-cta-ping" aria-hidden />}
          </button>
        )}
        {/* 2. Brief / 3. Elaborate */}
        {showResponseMode && (
          <div className="rch-mode-toggle" role="tablist" aria-label="Response mode" data-testid="rch-mode-toggle">
            <button
              type="button"
              role="tab"
              aria-selected={responseMode === "brief"}
              className={`rch-mode-tab ${responseMode === "brief" ? "is-active" : ""}`}
              onClick={() => onSetResponseMode?.("brief")}
              data-testid="rch-mode-brief"
            >
              <Layers className="w-3 h-3" aria-hidden /> Brief
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={responseMode === "elaborate"}
              className={`rch-mode-tab ${responseMode === "elaborate" ? "is-active" : ""}`}
              onClick={() => onSetResponseMode?.("elaborate")}
              data-testid="rch-mode-elaborate"
            >
              <FileText className="w-3 h-3" aria-hidden /> Elaborate
            </button>
          </div>
        )}
        {/* 4. Mute / Speaker */}
        <button
          className="rch-hdr-icon-btn"
          onClick={onToggleTts}
          aria-label={ttsOn ? "Mute responses" : "Read responses aloud"}
          data-testid="tts-toggle"
        >
          {ttsOn ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
        </button>
        {!isEmbed && isInternalRudras && (
          <Link to="/demo" className="rch-hdr-build-btn" data-testid="rch-header-build-agent">
            <Sparkles className="w-3.5 h-3.5" />
            <span>Build my AI Agent</span>
          </Link>
        )}
        {/* 5. Get in touch */}
        {!isEmbed && (
          <button
            className="rch-hdr-btn rch-hdr-cta"
            onClick={onOpenLead}
            data-testid="rch-header-cta"
          >
            Get in touch
          </button>
        )}
        {/* 6. Exit — closes the chat and returns to the marketing landing */}
        {!isEmbed && typeof onExit === "function" && (
          <button
            type="button"
            className="rch-hdr-exit"
            onClick={onExit}
            data-testid="rch-header-exit"
            aria-label="Exit chat"
            title="Exit chat"
          >
            <LogOut className="w-3.5 h-3.5" />
            <span>Exit</span>
          </button>
        )}
      </div>
    </header>
  );
}
